/**
 * `<embrace-chat>` — root custom-element van de webchat-widget.
 *
 * ## Verantwoordelijkheden
 *
 *   1. **Paneel-frame + launcher-host** — slot-host voor header/body/footer
 *      en plaatsing van `<embrace-chat-launcher>` rechtsonder.
 *   2. **Event-routing** — vangt user-events uit child-components en
 *      vertaalt ze naar `state.dispatch(action)`. Geen state-mutatie buiten
 *      de reducer.
 *   3. **Drag-drop + dropzone-overlay** — paneel-level drag-drop omdat
 *      child-components niet weten of ze drop-targets mogen zijn.
 *
 * ## Wat NIET hier woont
 *
 *   - State-machine logica → `src/state/reducer.ts`
 *   - Side-effects (polling, persistence, /leave-call) → `src/state/controller.ts`
 *   - Business-rules (canDownload, canAcceptFiles, unread-count) →
 *     `src/state/selectors.ts`
 *   - Render-routing per view → `src/views/panel-views.ts`
 *   - Launcher-knop + indicator → `src/components/chat-launcher.ts`
 *   - UI-copy → `src/i18n/nl.ts`
 *
 * Configuratie via `window.chatConfig` op de host-page (zie README.md).
 */
import { LitElement, css, html, nothing } from 'lit';
import { customElement, query, state } from 'lit/decorators.js';
import { tokens, ensureFigtreeFont } from './styles/tokens';
import { ChatStateController } from './state/controller';
import { AutoScrollController } from './controllers/auto-scroll-controller';
import {
  canAcceptFiles,
  derivePanelLayout,
  resolveChannels,
  resolveDefaultChannel,
  selectSelectedContract,
  selectSelectedUnitId,
  selectUnreadOperatorCount,
} from './state/selectors';
import type { ContractDetailed } from './api/contracts';
import { StubChatApiService } from './api/stub';
import { HttpChatApiService } from './api/http';
import type { ChatApiService } from './api/service';
import type { PendingMessage, ServiceStatus } from './state/types';
import { mapFilesToAttachments } from './utils/file-staging';
import { MAX_FILE_SIZE_BYTES, fileToDataUri } from './utils/file-data-uri';
import { downloadTranscript } from './utils/transcript';
import { makeLocalId } from './utils/id';
import {
  renderBody,
  renderFooter,
  renderHeader,
  type PanelViewDeps,
} from './views/panel-views';
import './components/chat-panel';
import './components/chat-launcher';
import type {
  ChannelId,
  ChannelOverrides,
} from './components/channel-picker';
import { setAssistantName } from './i18n/nl';

type ChatConfig = {
  /**
   * Base-URL voor de Embrace chat-server (KCC) — alle conversation-endpoints
   * (`/service/status`, `/conversations/*`) zijn relatief hieraan. Zonder
   * dit veld draait de widget in `StubChatApiService` (in-memory simulatie,
   * alleen voor dev/demo).
   */
  chatApiBaseUrl?: string;
  /** Extra config voor HTTP-API. */
  openingHours?: ServiceStatus['openingHours'];

  mockServiceStatus?: ServiceStatus;
  officeHoursText?: string;
  contactEmail?: string;
  orgName?: string;
  /**
   * Naam waarmee de virtuele assistent zich voorstelt en als label in de
   * chat-stream + transcript verschijnt. Default `'Digitale assistent'`.
   * Voorbeelden: `'Lisa'`, `'AAVA'`, `'Woon-Wijzer'`.
   *
   * NB: assistant-balloons die al eerder zijn gestuurd bevatten een snapshot
   * van de tekst van dat moment; een mid-conversation rename werkt alleen
   * door voor toekomstige messages.
   */
  assistantName?: string;
  channels?: ChannelId[];
  whatsappNumber?: string;
  messengerPage?: string;
  /**
   * Volledige URL naar het identity-endpoint van de host-portal. Wanneer
   * gezet, vraagt de chat zelf aan de portal of de bezoeker ingelogd is
   * (lazy bij channel-pick) en draait een authenticated session-request
   * met Bearer-token. Zonder deze URL valt de widget terug op het
   * anonieme pad — handig voor sites zonder login.
   *
   * Voorbeeld voor Embrace-Umbraco-sites:
   *   identityUrl: 'https://onlycustomers.umbraco13.stable-alpha.embracecloud.io/umbraco/surface/chat/identity'
   *
   * Contract met de portal: GET, cookies `credentials: 'include'`,
   * response `{ JWT?: string }`. Zie src/api/identity.ts voor details.
   */
  identityUrl?: string;
  /**
   * Optionele inlog-URL voor de "Inloggen"-knop op het auth-required-
   * scherm. Zonder URL toont de widget alleen een "Terug"-actie.
   */
  repairLoginUrl?: string;
  /**
   * Portal-route naar de reparatieverzoek-pagina. Doel van de "Nee, niet
   * opgelost"-tak van de self-fix-vraag. Zonder URL blijft de chat open.
   */
  repairRequestUrl?: string;
  /**
   * Base-URL voor de Embrace Customers REST API. Wordt gebruikt door de
   * AVAA-intake-flow om de actieve huurcontracten van de ingelogde
   * bezoeker op te halen (`GET {customersApiBaseUrl}/v1/contract/active/me/paged`)
   * met de JWT uit de identity-check als Bearer-token.
   *
   * Zonder deze URL slaat de AVAA-flow de contract-fetch over en biedt
   * de bezoeker alleen de greeting + composer aan (legacy POC-pad voor
   * sites zonder Customers-API-toegang).
   */
  customersApiBaseUrl?: string;
  /**
   * Base-URL voor de AAVA Intake Conversation API. De widget plakt
   * zelf `/aava-repairs/api/v1/conversation/...` paths erachter, dus
   * deze waarde moet de **bare host** zijn (zonder `/api`-suffix).
   *
   * Voorbeeld voor staging-alpha:
   *   `https://onlycustomers.customersbase.alpha.embracecloud.io`
   *
   * Zonder deze URL slaat de AVAA-intake-flow de API-call over —
   * bezoeker ziet wel de UI maar krijgt geen response van de AI.
   */
  aavaApiBaseUrl?: string;
  /**
   * Portal-route waar de bezoeker naartoe gaat zodra de AAVA-intake
   * server-side is afgerond (`finishedAt` gezet). De widget pollt de
   * conversation via de `/aava`-proxy en navigeert bij afronding naar
   * `{repairCompletionUrl}/{scenarioId}/{conversationId}` — exact het
   * PoC-`chat-voltooid`-patroon. Zonder deze URL pollt de widget niet en
   * blijft de conversation in de chat (geen redirect).
   *
   * Voorbeeld: `/reparatie/nieuw/chat-voltooid`
   */
  repairCompletionUrl?: string;
  /**
   * `scenarioId` voor de completion-redirect. Fallback voor wanneer de
   * server geen `externalId` op de conversation teruggeeft. Samen met
   * `repairCompletionUrl` bouwt de widget de `chat-voltooid`-route.
   */
  repairScenarioId?: string;
  /**
   * Spoednummer in de AI-gebruiksvoorwaarden ("…neem direct contact op via
   * {dit nummer}…"). Vervangt de `[spoednummer]`-placeholder. Leeg → nette
   * fallback ("het storingsnummer van {orgName}").
   */
  repairEmergencyNumber?: string;
  /**
   * Kanaal/omschrijving voor support in de AI-gebruiksvoorwaarden ("…contact
   * opnemen met een medewerker van de klantenservice via {dit kanaal}…").
   * Vervangt de `[kanaal/livechat]`-placeholder. Leeg → fallback
   * ("telefoon of e-mail").
   */
  repairSupportChannel?: string;
  /**
   * Per-kanaal overrides voor naam, blurb, kleuren, SLA-tekst en/of
   * logo-URL. Velden die niet gezet zijn vallen terug op de built-in
   * defaults — host kan dus alleen specificeren wat anders moet.
   */
  channelOverrides?: ChannelOverrides;
  /**
   * Zet de emoji-knop in de composer aan/uit. Sommige corporaties willen
   * geen emoji's in hun klantcontact. Default `true` (knop zichtbaar).
   * `false` → de emoji-knop verdwijnt volledig uit de composer.
   */
  enableEmoji?: boolean;
};

/**
 * Detail van het `contract-selected` custom-event dat de chat dispatch't
 * zodra de bezoeker een contract kiest in de AVAA-flow. Bubbelt + composed
 * = true zodat host-pagina-listeners (buiten de shadow-DOM) het oppakken.
 *
 * Voorbeeld:
 *   document.querySelector('embrace-chat').addEventListener(
 *     'contract-selected',
 *     (e) => startAavaIntake(e.detail.unitId),
 *   );
 */
export type ContractSelectedEventDetail = {
  contractId: string;
  /** Eenheid-Guid uit het gekozen contract. `null` als de unit ontbrak. */
  unitId: string | null;
  /** Volledig contract-object — voor zeldzame extra velden. */
  contract: ContractDetailed;
};

/**
 * Globale, framework-onafhankelijke besturings-API op `window.webchat` —
 * aanroepbaar vanaf elke plek in de host-pagina. Alle methodes geven een
 * Boolean terug zodat de host een fallback kan doen wanneer het widget
 * (nog) niet gemonteerd is.
 */
export type WebchatApi = {
  /** Open het paneel. `true` = geopend of al open; `false` = widget niet gemonteerd. */
  open(): boolean;
  /** Sluit/minimaliseer het paneel. `true` = gesloten of al gesloten; `false` = niet gemonteerd. */
  close(): boolean;
  /** Beëindig het gesprek (zoals de knop "Chatgesprek sluiten"). `true` = er was een
   *  gesprek dat beëindigd is; `false` = niets te beëindigen of widget niet gemonteerd. */
  endConversation(): boolean;
};

declare global {
  interface Window {
    chatConfig?: ChatConfig;
    webchat?: WebchatApi;
  }
  interface HTMLElementEventMap {
    'contract-selected': CustomEvent<ContractSelectedEventDetail>;
  }
}

@customElement('embrace-chat')
export class EmbraceChat extends LitElement {
  private readonly state = new ChatStateController(this, this.createApi(), {
    identity: { url: window.chatConfig?.identityUrl ?? null },
    customersApiBaseUrl: window.chatConfig?.customersApiBaseUrl ?? null,
    aavaApiBaseUrl: window.chatConfig?.aavaApiBaseUrl ?? null,
    repairCompletionUrl: window.chatConfig?.repairCompletionUrl ?? null,
    repairScenarioId: window.chatConfig?.repairScenarioId ?? null,
    repairRequestUrl: window.chatConfig?.repairRequestUrl ?? null,
    // Bij single-channel-config is er geen picker-UI; controller pickt
    // dan zelf zodat de identity-flow + intake-form-flow consistent
    // werken alsof de bezoeker doorgeklikt had.
    defaultChannel: resolveDefaultChannel(
      resolveChannels(window.chatConfig?.channels),
    ),
  });

  @query('embrace-message-list')
  private messageListEl?: HTMLElement;

  @state()
  private dragOver = false;

  @query('input[type="file"]')
  private fileInputEl?: HTMLInputElement;

  private readonly autoScroll = new AutoScrollController(this, () =>
    this.messageListEl?.shadowRoot?.querySelector<HTMLElement>('.list') ?? null,
  );

  constructor() {
    super();
    // Vóór de eerste reducer-dispatch zetten zodat alle i18n-getters
    // direct de juiste naam zien.
    const customName = window.chatConfig?.assistantName;
    if (customName) setAssistantName(customName);
  }

  private createApi(): ChatApiService {
    const config = window.chatConfig;
    // Als er een chatApiBaseUrl is geconfigureerd → praat tegen de echte
    // backend. Zo niet → stub-mode (walking-skeleton + demos).
    if (config?.chatApiBaseUrl) {
      return new HttpChatApiService({
        chatApiBaseUrl: config.chatApiBaseUrl,
        openingHours: config.openingHours ?? config.mockServiceStatus?.openingHours,
      });
    }
    return new StubChatApiService({
      serviceStatus: config?.mockServiceStatus,
    });
  }

  static override styles = [
    tokens,
    css`
      :host {
        display: contents;
      }

      .bootstrap {
        padding: 28px 22px;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 8px;
        flex: 1;
        color: #5E5F62;
        font: 500 13px/1.4 var(--ec-font-family);
      }

      .dropzone-overlay {
        position: fixed;
        right: var(--ec-offset-x);
        bottom: calc(var(--ec-offset-y) + var(--ec-launcher-size) + 16px);
        width: var(--ec-panel-width);
        max-width: calc(100vw - 2 * var(--ec-offset-x));
        height: var(--ec-panel-max-height);
        max-height: calc(100vh - 120px);
        z-index: var(--ec-z-index);
        border-radius: var(--ec-radius-panel);
        background: rgba(12, 28, 71, .82);
        color: #fff;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 10px;
        pointer-events: none;
        font: 600 16px/1.3 var(--ec-font-display);
        backdrop-filter: blur(4px);
      }
      .dropzone-overlay small {
        font: 400 13px/1.4 var(--ec-font-family);
        opacity: .75;
      }

      /* CSS-grid stack: searching-banner en composer delen één cel; de
         composer slidet onder de banner vandaan via data-stage="active". */
      .footer-stack {
        display: grid;
        grid-template-columns: 1fr;
        position: relative;
      }
      /* Tijdens searching: clip de translateY(100%)-composer zodat hij niet
         onder het paneel uitsteekt. Tijdens active GEEN clipping, anders
         knipt het de emoji-popover die boven de composer hoort te zweven. */
      .footer-stack:not([data-stage="active"]) {
        overflow: hidden;
      }
      .footer-stack > * {
        grid-column: 1;
        grid-row: 1;
        /* Grid items hebben default min-width:auto (= min-content). Als
           de composer's intrinsieke min-content groter is dan de stack-
           breedte (door textarea + buttons-rij), rekt het grid de track
           op voorbij de container. min-width:0 laat items krimpen tot
           binnen de cel. Zelfde voor min-height bij hoge content. */
        min-width: 0;
        min-height: 0;
      }
      .footer-stack > embrace-searching-banner {
        z-index: 1;
        opacity: 1;
        transition: opacity 150ms ease-out;
      }
      .footer-stack > embrace-chat-composer {
        z-index: 2;
        transform: translateY(100%);
        transition: transform 200ms ease-out;
        /* In searching-stage mag de composer geen events vangen — hij
           is dan visueel niet aanwezig. */
        pointer-events: none;
      }
      .footer-stack[data-stage="active"] > embrace-chat-composer {
        transform: translateY(0);
        pointer-events: auto;
      }
      .footer-stack[data-stage="active"] > embrace-searching-banner {
        opacity: 0;
        pointer-events: none;
        /* Banner faded pas weg nadat de composer halverwege is — anders
           ontstaat er een kort moment waar de footer-area visueel leeg
           lijkt vóór de composer in beeld komt. */
        transition-delay: 100ms;
      }

      @media (prefers-reduced-motion: reduce) {
        .footer-stack > embrace-chat-composer,
        .footer-stack > embrace-searching-banner {
          transition: none;
        }
      }
    `,
  ];

  override connectedCallback(): void {
    super.connectedCallback();
    ensureFigtreeFont();
  }

  // ===========================================================================
  // Public imperative API
  // ===========================================================================

  /**
   * Opent het paneel programmatisch en (optioneel) preselecteert een kanaal,
   * zodat de bezoeker de channel-picker overslaat. Bedoeld voor host-pagina-
   * triggers zoals een "Reparatieverzoek"-link of CTA-knop.
   *
   * Voorbeeld:
   *   document.querySelector('embrace-chat').openChat({ channel: 'webchat' });
   *
   * Edge-case: als de bezoeker al in een lopende conversatie zit (queued /
   * active / requesting / bot-flow), respecteren we die en doen we alléén
   * de panel-open. We resetten geen lopende sessie — dat zou het gesprek
   * stilletjes weggooien zonder bevestiging.
   */
  openChat(opts: { channel?: ChannelId } = {}): boolean {
    const s = this.state.get();
    const inConversation =
      s.session.phase !== 'none' || s.assistant !== null || s.intake !== null;

    if (!s.ui.panelOpen) {
      this.state.dispatch({ type: 'PANEL_OPENED' });
    }

    if (opts.channel && !inConversation && s.ui.pickedChannel !== opts.channel) {
      this.state.dispatch({ type: 'CHANNEL_PICKED', channel: opts.channel });
    }
    // Geopend of al open (incl. met een lopend gesprek).
    return true;
  }

  /**
   * Sluit/minimaliseer het paneel. Idempotent — al gesloten telt als
   * succes. Het gesprek blijft bestaan; alleen de UI gaat dicht.
   * Returns `true` (gesloten of al gesloten).
   */
  closeChat(): boolean {
    if (this.state.get().ui.panelOpen) {
      this.state.dispatch({ type: 'PANEL_CLOSED' });
    }
    return true;
  }

  /**
   * Beëindig het gesprek programmatisch — exact wat de "Chatgesprek
   * sluiten"-knop doet (zie {@link handleEndConversation}). Returns `true`
   * wanneer er daadwerkelijk een gesprek was om te beëindigen.
   */
  endConversation(): boolean {
    return this.endConversationInternal();
  }

  /**
   * Het volledige contract dat de bezoeker heeft gekozen in de AVAA-flow.
   * `null` zolang er nog geen selectie is (of de bezoeker niet in de
   * `repair-request`-flow zit).
   *
   * Bedoeld voor de AVAA-intake-integratie: zodra de bezoeker zijn
   * vraag indient kan host-code dit contract meesturen naar de AI-API
   * via een eigen event-listener of polling van deze property.
   */
  get selectedContract(): ContractDetailed | null {
    return selectSelectedContract(this.state.get());
  }

  /**
   * Shortcut voor `selectedContract?.unit?.id` — de eenheid-Guid die
   * de AVAA-intake nodig heeft om een gesprek voor de juiste woning op
   * te zetten. Returns `null` wanneer geen selectie of het contract
   * geen unit heeft (defensief — Customers-API kan `unit: null` leveren).
   *
   * Voorbeeld voor host-integratie:
   *   const unitId = document.querySelector('embrace-chat').selectedUnitId;
   *   if (unitId) startAavaIntake(unitId);
   */
  get selectedUnitId(): string | null {
    return selectSelectedUnitId(this.state.get());
  }

  override willUpdate(): void {
    this.autoScroll.measure();
  }

  override updated(): void {
    this.autoScroll.applyIfStuck();
  }

  // ===========================================================================
  // Render
  // ===========================================================================

  override render() {
    const s = this.state.get();
    const showDropzone = this.dragOver && s.ui.panelOpen && canAcceptFiles(s);
    // derivePanelLayout is single source of truth voor view-routing —
    // header, body en footer kijken alle drie naar dezelfde view-enum
    // i.p.v. eigen if-cascades te dupliceren (was bron van body-vs-footer
    // mismatch-bugs).
    const layout = derivePanelLayout(
      s,
      resolveChannels(window.chatConfig?.channels),
    );
    const deps = this.buildViewDeps(s);

    return html`
      <embrace-chat-panel
        ?open=${s.ui.panelOpen}
        @dragover=${this.handleDragOver}
        @dragleave=${this.handleDragLeave}
        @drop=${this.handleDrop}
      >
        ${renderHeader(layout, deps)}
        ${renderBody(layout, deps)}
        ${renderFooter(layout, deps)}
      </embrace-chat-panel>

      ${showDropzone ? this.renderDropzoneOverlay() : nothing}

      <input
        type="file"
        hidden
        multiple
        @change=${this.handleFileInputChange}
      />

      <embrace-chat-launcher
        ?panelOpen=${s.ui.panelOpen}
        .unreadCount=${selectUnreadOperatorCount(s)}
        .online=${s.serviceStatus
          ? s.serviceStatus.isAcceptingConversationRequests
          : null}
        @click=${this.handleLauncherClick}
      ></embrace-chat-launcher>
    `;
  }

  /**
   * Bouwt het deps-object dat de panel-views nodig hebben. Bewust per
   * `render()` opnieuw — handlers zijn arrow-functies (stable reference),
   * config-strings zijn O(1) reads, dus de allocatie is verwaarloosbaar
   * vergeleken met de zekerheid dat ze altijd aktueel zijn.
   */
  private buildViewDeps(s: ReturnType<ChatStateController['get']>): PanelViewDeps {
    const config = window.chatConfig;
    return {
      state: s,
      orgName: config?.orgName ?? 'Klantenservice',
      contactEmail: config?.contactEmail ?? '',
      whatsappNumber: config?.whatsappNumber ?? '',
      messengerPage: config?.messengerPage ?? '',
      channelOverrides: config?.channelOverrides,
      repairLoginUrl: config?.repairLoginUrl,
      repairEmergencyNumber: config?.repairEmergencyNumber ?? '',
      repairSupportChannel: config?.repairSupportChannel ?? '',
      enableEmoji: config?.enableEmoji ?? true,
      onClosePanel: this.handleClosePanel,
      onBackToChannelPicker: this.handleBackToChannelPicker,
      onDownloadTranscript: this.handleDownloadTranscript,
      onIntakeSubmit: this.handleIntakeSubmit,
      onUserSubmit: this.handleUserSubmit,
      onOpenFilePicker: this.handleOpenFilePicker,
      onRemoveStaged: this.handleRemoveStaged,
      onEndConversation: this.handleEndConversation,
      onStartNewConversation: this.handleStartNewConversation,
      onPickChannel: this.handlePickChannel,
      onPickerDrilldownChat: this.handlePickerDrilldownChat,
      onPickerBackToRoot: this.handlePickerBackToRoot,
      onRateSubmit: this.handleRateSubmit,
      onVisitorForget: this.handleVisitorForget,
      onRetryMessage: this.handleRetryMessage,
      onContractSelect: this.handleContractSelect,
      onContractRetry: this.handleContractRetry,
      onCaptchaVerified: this.handleCaptchaVerified,
      onSelfFixConfirm: this.handleSelfFixConfirm,
      onSelfFixDecline: this.handleSelfFixDecline,
    };
  }

  private readonly handleSelfFixConfirm = (): void => {
    this.state.onSelfFixConfirm();
  };

  private readonly handleSelfFixDecline = (): void => {
    this.state.onSelfFixDecline();
  };

  private readonly handleCaptchaVerified = (
    event: CustomEvent<{ token: string }>,
  ): void => {
    this.state.dispatch({
      type: 'CAPTCHA_VERIFIED',
      captchaToken: event.detail.token,
    });
  };

  private renderDropzoneOverlay() {
    return html`
      <div class="dropzone-overlay" part="dropzone">
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="M21.44 11.05l-9.19 9.19a5.5 5.5 0 0 1-7.78-7.78l9.19-9.19a3.5 3.5 0 0 1 4.95 4.95l-8.49 8.49a1.5 1.5 0 0 1-2.12-2.12l7.78-7.78"/>
        </svg>
        Laat los om bestand toe te voegen
        <small>Foto's, PDF's of documenten</small>
      </div>
    `;
  }

  // ===========================================================================
  // Event handlers
  // ===========================================================================

  private handleLauncherClick(): void {
    const { ui } = this.state.get();
    this.state.dispatch({
      type: ui.panelOpen ? 'PANEL_CLOSED' : 'PANEL_OPENED',
    });
  }

  /*
   * Handlers gepasseerd via `buildViewDeps` zijn arrow-fn class-fields
   * (auto-binden `this`). Inline-handlers in `render()` (handleDragOver
   * etc.) blijven gewone methods omdat Lit ze zelf bindt op de host.
   */

  private readonly handleClosePanel = (): void => {
    this.state.dispatch({ type: 'PANEL_CLOSED' });
  };

  private readonly handleContractSelect = (
    event: CustomEvent<{ contractId: string }>,
  ): void => {
    // Detecteer een échte selectie-wijziging door pre/post-state te
    // vergelijken — de reducer kan de actie als no-op behandelen (zelfde
    // id, onbekend id) en dan willen we GEEN outbound event vuren.
    const prevId = this.state.get().contracts.selectedId;
    this.state.dispatch({
      type: 'CONTRACT_SELECTED',
      contractId: event.detail.contractId,
    });
    const next = this.state.get();
    const nextId = next.contracts.selectedId;
    if (nextId === prevId || nextId === null) return;
    const contract = next.contracts.items.find((c) => c.id === nextId);
    if (!contract) return;
    // Bubble + composed = true zodat host-pagina-listeners (die buiten
    // de shadow-DOM hangen) het event opvangen. Detail bevat zowel de
    // contractId als de afgeleide unitId — caller hoeft het object niet
    // zelf te traversen voor de meest gevraagde waarde.
    this.dispatchEvent(
      new CustomEvent('contract-selected', {
        detail: {
          contractId: nextId,
          unitId: contract.unit?.id ?? null,
          contract,
        },
        bubbles: true,
        composed: true,
      }),
    );
  };

  private readonly handleContractRetry = (): void => {
    // Retry-knop verschijnt in de inline contract-choice-block tijdens
    // 'aava-selecting-contract' wanneer de fetch faalde. Buiten die step
    // is een retry niet zinvol (geen UI om 't af te beelden).
    const s = this.state.get();
    if (s.assistant?.step !== 'aava-selecting-contract') return;
    this.state.retryContractFetch();
  };

  private readonly handleEndConversation = (): void => {
    this.endConversationInternal();
  };

  /**
   * Gedeelde gesprek-beëindiging voor de knop én de `window.webchat`-API.
   * Twee paden:
   *   - Lopende sessie (queued/active/requesting): SESSION_ENDED. UI gaat
   *     naar ended-state, controller fired best-effort /leave zodat de
   *     operator de afhaak direct ziet.
   *   - Pre-session (bot-flow loopt nog, géén session-id): SESSION_RESET.
   *     Niets serverside te beëindigen — alleen client-state opruimen en
   *     terug naar channel-picker / intake.
   * Returns `true` wanneer er een gesprek (server-sessie óf bot-flow/intake)
   * was om te beëindigen; `false` wanneer er in feite niets liep (de reset
   * draait dan nog steeds, zodat de UI gegarandeerd schoon is).
   */
  private endConversationInternal(): boolean {
    const s = this.state.get();
    const hasServerSession =
      s.session.phase === 'queued' ||
      s.session.phase === 'active' ||
      s.session.phase === 'requesting';
    if (hasServerSession) {
      this.state.dispatch({ type: 'SESSION_ENDED', reason: 'customer' });
      return true;
    }
    const hadConversation =
      s.session.phase !== 'none' || s.assistant !== null || s.intake !== null;
    this.state.dispatch({ type: 'SESSION_RESET' });
    return hadConversation;
  }

  private readonly handleStartNewConversation = (): void => {
    // SESSION_RESET wist serverMessages, pendingMessages, assistant-state,
    // visitor, intake én pickedChannel. localStorage wordt door de
    // controller gewist. Resultaat: bezoeker ziet de intake-form (of
    // channel-picker als er meerdere kanalen zijn).
    this.state.dispatch({ type: 'SESSION_RESET' });
  };

  private readonly handleIntakeSubmit = (
    event: CustomEvent<{ question: string; captchaToken: string }>,
  ): void => {
    // Geen directe session-request meer — eerst de virtuele assistent
    // doorlopen om naam + e-mail te verzamelen.
    this.state.dispatch({
      type: 'INTAKE_QUESTION_RECEIVED',
      question: event.detail.question,
      captchaToken: event.detail.captchaToken,
    });
  };

  private readonly handleRateSubmit = (
    event: CustomEvent<{ rating: number }>,
  ): void => {
    this.state.dispatch({ type: 'RATING_SUBMITTED', rating: event.detail.rating });
  };

  private readonly handleRetryMessage = (
    event: CustomEvent<{ localId: string }>,
  ): void => {
    this.state.dispatch({
      type: 'MESSAGE_RETRY_REQUESTED',
      localId: event.detail.localId,
    });
  };

  private readonly handleVisitorForget = (): void => {
    this.state.dispatch({ type: 'VISITOR_FORGOTTEN' });
  };

  private readonly handleUserSubmit = (
    event: CustomEvent<{ text: string }>,
  ): void => {
    const text = event.detail.text;
    const s = this.state.get();
    // Een bijlage zonder begeleidende tekst mag óók verstuurd worden
    // (WI 164914) — een foto van de storing is op zichzelf een bericht.
    // Alleen droppen wanneer er werkelijk niets is: geen tekst én geen
    // staged bestand.
    if (!text && s.stagedFiles.length === 0) return;

    // Na een afgeronde AAVA-intake (redirect onderweg) geen input meer
    // accepteren — de composer is gedimd maar een programmatic submit /
    // race zou anders alsnog een bericht proberen te sturen.
    if (s.aava.finished) return;

    // Bot-Q&A alleen voor de actieve vraag-stappen. Vanaf 'searching' is de
    // bot klaar met data verzamelen — alles wat de bezoeker daarna typt is
    // een echt chat-bericht en moet gequeued worden voor de operator.
    // 'aava-asking' is de AI-intake voor authorized repair-request: ook
    // pre-sessie, dus tekst gaat naar de reducer i.p.v. naar de server.
    if (
      s.session.phase === 'none' &&
      (s.assistant?.step === 'asking-question' ||
        s.assistant?.step === 'asking-name' ||
        s.assistant?.step === 'asking-email' ||
        s.assistant?.step === 'aava-asking')
    ) {
      this.state.dispatch({ type: 'ASSISTANT_USER_REPLIED', text });
      return;
    }
    // 'aava-selecting-contract': composer is disabled in de UI maar voor
    // de zekerheid (programmatic submit, race-condition) droppen we input
    // hier expliciet — de bezoeker moet eerst klikken op een card.
    if (s.assistant?.step === 'aava-selecting-contract') return;

    // Queue tijdens searching/requesting/queued/active. postMessage in de
    // controller skipt de POST tijdens searching/requesting (geen session-id);
    // SESSION_STARTED-reactor flushed de pending-messages alsnog zodra de
    // sessie staat.
    const queueable =
      s.session.phase === 'requesting' ||
      s.session.phase === 'queued' ||
      s.session.phase === 'active' ||
      (s.session.phase === 'none' && s.assistant?.step === 'searching');
    if (!queueable) return;

    const message: PendingMessage = {
      localId: makeLocalId(),
      text,
      timestamp: Date.now(),
      status: 'sending',
      files: s.stagedFiles.length > 0 ? [...s.stagedFiles] : undefined,
    };
    this.state.dispatch({ type: 'MESSAGE_QUEUED', message });
  };

  // ===========================================================================
  // Channel picker
  // ===========================================================================

  private readonly handlePickChannel = (
    event: CustomEvent<{ channel: ChannelId }>,
  ): void => {
    this.state.dispatch({ type: 'CHANNEL_PICKED', channel: event.detail.channel });
  };

  private readonly handleBackToChannelPicker = (): void => {
    // Bot-flow / intake-state is alleen geldig binnen de webchat-context.
    // Als die nog actief is, kiezen we voor SESSION_RESET (wist tegelijk
    // pickedChannel) — anders volstaat een losse CHANNEL_PICKED met null.
    const s = this.state.get();
    if (s.assistant !== null || s.intake !== null) {
      this.state.dispatch({ type: 'SESSION_RESET' });
    } else {
      this.state.dispatch({ type: 'CHANNEL_PICKED', channel: null });
    }
  };

  private readonly handlePickerDrilldownChat = (): void => {
    this.state.dispatch({ type: 'PICKER_DRILLDOWN_CHAT' });
  };

  private readonly handlePickerBackToRoot = (): void => {
    this.state.dispatch({ type: 'PICKER_BACK_TO_ROOT' });
  };

  // ===========================================================================
  // File uploads
  // ===========================================================================

  private readonly handleOpenFilePicker = (): void => {
    this.fileInputEl?.click();
  };

  private handleFileInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    if (files.length > 0) void this.stageFiles(files);
    input.value = '';
  }

  private readonly handleRemoveStaged = (
    event: CustomEvent<{ fileId: string }>,
  ): void => {
    this.state.dispatch({
      type: 'STAGED_FILE_REMOVED',
      fileId: event.detail.fileId,
    });
  };

  private handleDragOver(event: DragEvent): void {
    if (!canAcceptFiles(this.state.get())) return;
    event.preventDefault();
    if (!this.dragOver) this.dragOver = true;
  }

  private handleDragLeave(): void {
    this.dragOver = false;
  }

  private handleDrop(event: DragEvent): void {
    event.preventDefault();
    this.dragOver = false;
    if (!canAcceptFiles(this.state.get())) return;
    const files = Array.from(event.dataTransfer?.files ?? []);
    if (files.length > 0) void this.stageFiles(files);
  }

  private async stageFiles(files: File[]): Promise<void> {
    // Per-file size-guard tegen de 10 MiB request-limit van AAVA's
    // /message endpoint (na base64-inflation). Bestanden boven de cap
    // worden gefilterd; we tonen één gecombineerde alert zodat de
    // bezoeker direct weet wat er weg is.
    const tooLarge = files.filter((f) => f.size > MAX_FILE_SIZE_BYTES);
    const accepted = files.filter((f) => f.size <= MAX_FILE_SIZE_BYTES);
    if (tooLarge.length > 0) {
      const maxMb = Math.round(MAX_FILE_SIZE_BYTES / (1024 * 1024));
      const names = tooLarge.map((f) => f.name).join(', ');
      // Simple alert volstaat voor dev — in productie kan dit naar een
      // toast/snackbar evolueren. Voor nu: blokkeren is belangrijker
      // dan elegant communiceren.
      alert(`Bestanden groter dan ${maxMb} MB worden niet ondersteund: ${names}`);
    }
    if (accepted.length === 0) return;

    const staged = mapFilesToAttachments(accepted);
    // DataUris alleen lezen wanneer we voorzien dat ze nodig zijn —
    // AAVA-flow heeft ze nodig (binnen-client send → API). Classic
    // chat-server-flow upload bytes via een eigen endpoint en heeft
    // ze hier niet nodig.
    const s = this.state.get();
    const needsDataUri = s.assistant?.step === 'aava-asking';
    if (needsDataUri) {
      try {
        const dataUris = await Promise.all(accepted.map(fileToDataUri));
        for (let i = 0; i < staged.length; i++) {
          staged[i]!.dataUri = dataUris[i]!;
        }
      } catch (err) {
        console.warn('[chat] file-to-dataUri faalde:', err);
        // Continue zonder dataUri — UI toont de file-chip wel, maar
        // bij submit zal de controller 'm overslaan (filter op
        // dataUri-aanwezigheid). Beter dan helemaal afbreken.
      }
    }
    this.state.dispatch({ type: 'FILES_STAGED', files: staged });
  }

  private readonly handleDownloadTranscript = (): void => {
    const s = this.state.get();
    const op =
      s.session.phase === 'active' || s.session.phase === 'ended'
        ? s.session.operator
        : null;
    downloadTranscript({
      serverMessages: s.serverMessages,
      assistantMessages: s.assistantMessages,
      operator: op,
      orgName: window.chatConfig?.orgName ?? 'Klantenservice',
    });
  };

}

declare global {
  interface HTMLElementTagNameMap {
    'embrace-chat': EmbraceChat;
  }
}

/**
 * Drop-in auto-mount voor deployment via Umbraco "Externe toolscripts" of
 * CDN-script-tag: bundle plaatst `<embrace-chat>` zelf in de DOM zodra
 * `window.chatConfig` aanwezig is. Geen `chatConfig` = stille no-op (ook
 * de signal die vitest-imports geen launcher in `document.body` laat
 * verschijnen). Bestaand element = embedder plaatste 'm zelf, niet
 * verdubbelen.
 */
export function autoMountChat(): void {
  if (typeof document === 'undefined') return;
  if (typeof window === 'undefined' || !window.chatConfig) return;
  if (document.querySelector('embrace-chat')) return;
  document.body.appendChild(document.createElement('embrace-chat'));
}

/** Het gemonteerde `<embrace-chat>`-element, of `null` als (nog) niet gemonteerd. */
function mountedChat(): EmbraceChat | null {
  if (typeof document === 'undefined') return null;
  return document.querySelector('embrace-chat');
}

/**
 * Installeert de globale `window.webchat`-besturings-API. Elke call zoekt
 * het live element op (zodat de API ook werkt als het widget later mount)
 * en valt terug op `false` wanneer er niets gemonteerd is.
 */
export function installWebchatApi(): void {
  if (typeof window === 'undefined') return;
  window.webchat = {
    open: () => mountedChat()?.openChat() ?? false,
    close: () => mountedChat()?.closeChat() ?? false,
    endConversation: () => mountedChat()?.endConversation() ?? false,
  };
}

if (typeof document !== 'undefined') {
  installWebchatApi();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', autoMountChat, { once: true });
  } else {
    autoMountChat();
  }
}
